import React, { FC } from 'react'
import { Field, InjectedFormProps, reduxForm } from 'redux-form'
import { Col, Form, Row } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce, get } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '../../../reducers'

// atoms
import InputField from '../../../atoms/InputField'
import SelectField from '../../../atoms/SelectField'
import TypeCheckboxGroupField from '../../../atoms/TypeCheckboxGroupField'

// interfaces
import { IForm } from '../../../types/interfaces'

// utils
import { FORM, FORM_TEMPLATE_TYPES } from '../../../utils/enums'

// helpers
import { validateRequired } from '../../../utils/helper'

// validate
// eslint-disable-next-line import/no-cycle
import validateGeneralSettingsForm from '../../../components/forms/validations/validateGeneralSettingsForm'
import CheckboxField from '../../../atoms/CheckboxField'

export type ComponentProps = {}

type Props = InjectedFormProps<IForm, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

const FormGeneralSettingsForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const { handleSubmit, change } = props
	const queue = useSelector((state: RootState) => state.formTemplate.queue)
	const databases = useSelector((state: RootState) => state.formTemplate.databases)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const procedures = useSelector((state: RootState) => state.formTemplate.procedures)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.FORM_GENERAL_SETTINGS]?.values)
	const hasInboundValue = get(formValues, 'type')
	const usesAutoDialing = get(formValues, 'usesAutoDialing')
	const usesAutoHangup = get(formValues, 'usesAutoHangup')
	const validation = hasInboundValue?.includes(FORM_TEMPLATE_TYPES.INBOUND) ? validateRequired : null

	const onChangeAutoDialing = (e: any) => {
		// NOTE: ak sa zrusi autodialing tak sa rusia aj nastavene hodnoty pre autoDialingDelay a autoDialsBestRecord
		if (!e.target.checked) {
			change('autoDialingDelay', null)
			change('autoDialsBestRecord', false)
		}
	}

	return (
		<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
			<Row justify={'center'} className={'mt-2 w-full'}>
				<Col span={24}>
					<Field
						component={InputField}
						label={t('loc:Form name')}
						placeholder={t('loc:Enter name')}
						name={'name'}
						size={'large'}
						disabled={disable}
						required
					/>
				</Col>
				<Col span={24}>
					<Field
						component={SelectField}
						label={t('loc:Daktela front')}
						placeholder={t('loc:Select front')}
						name={'queue'}
						loading={queue?.isLoading}
						options={queue?.data}
						size={'large'}
						disabled={disable}
						required
					/>
				</Col>
				<Col span={24}>
					<Field
						component={SelectField}
						label={t('loc:Database')}
						placeholder={t('loc:Select database')}
						name={'database'}
						loading={databases?.isLoading}
						options={databases?.data}
						size={'large'}
						disabled={disable}
						required
					/>
				</Col>
				<Col className={'mt-1'} span={24}>
					<Field component={TypeCheckboxGroupField} name={'type'} size={'large'} disabled={disable} required />
				</Col>
				<Col span={24}>
					<Row gutter={[8, 8]}>
						<Col span={12}>
							<Field
								className={'bordered-checkbox'}
								component={CheckboxField}
								name={'usesAutoDialing'}
								label={t('loc:Use auto dialing')}
								placeholder={t('loc:Auto dialing')}
								onChange={onChangeAutoDialing}
								disabled={disable}
							/>
						</Col>
						<Col span={12}>
							<Field
								component={InputField}
								name={'autoDialingDelay'}
								label={t('loc:Auto dialing delay')}
								placeholder={t('loc:Auto dialing delay time')}
								size={'medium'}
								disabled={!usesAutoDialing || disable}
								type={'number'}
								maxLength={3}
							/>
						</Col>
					</Row>
				</Col>
				<Col span={24}>
					<Row gutter={[8, 8]}>
						<Col span={12}>
							<Field
								className={'bordered-checkbox'}
								component={CheckboxField}
								name={'usesAutoHangup'}
								label={t('loc:Use auto hang up')}
								placeholder={t('loc:Auto hang up')}
								onChange={onChangeAutoDialing}
								disabled={disable}
							/>
						</Col>
						<Col span={12}>
							<Field
								component={InputField}
								name={'autoHangupDelay'}
								label={t('loc:Ring time')}
								placeholder={t('loc:Add ring time')}
								size={'medium'}
								disabled={!usesAutoHangup || disable}
								type={'number'}
								maxLength={3}
							/>
						</Col>
					</Row>
				</Col>
				<Col span={24}>
					<Field
						className={'bordered-checkbox'}
						component={CheckboxField}
						name={'autoDialsBestRecord'}
						label={t('loc:Select best record when auto dialing')}
						placeholder={t('loc:Best record')}
						disabled={!usesAutoDialing || disable}
					/>
				</Col>
				{hasInboundValue?.includes(FORM_TEMPLATE_TYPES.INBOUND) && (
					<>
						<Col span={24}>
							<Field
								component={SelectField}
								label={t('loc:Search stored procedure')}
								name={'searchStoredProcedure'}
								loading={procedures?.isLoading}
								options={procedures?.data}
								size={'large'}
								clearBtn={true}
								validate={validation}
								disabled={disable}
								required
							/>
						</Col>
						<Col span={24}>
							<Field
								component={SelectField}
								label={t('loc:Retrieve stored procedure')}
								name={'retrieveStoredProcedure'}
								loading={procedures?.isLoading}
								options={procedures?.data}
								size={'large'}
								clearBtn={true}
								validate={validation}
								disabled={disable}
								required
							/>
						</Col>
						<Col span={24}>
							<Field
								component={SelectField}
								label={t('loc:Insert stored procedure')}
								name={'insertStoredProcedure'}
								loading={procedures?.isLoading}
								options={procedures?.data}
								size={'large'}
								clearBtn={true}
								validate={validation}
								disabled={disable}
								required
							/>
						</Col>
					</>
				)}
			</Row>
		</Form>
	)
}

const form = reduxForm<IForm, ComponentProps>({
	form: FORM.FORM_GENERAL_SETTINGS,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateGeneralSettingsForm,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, 900)
})(FormGeneralSettingsForm)

export default form
